import type { FC, MutableRefObject } from 'react'

import { memo, useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { mix } from '@/lib/color'
import { playChime, playTick } from '@/lib/synth'

export type LandEvent = {
    bin: number
    multiplier: number
    intensity: number
}

export type GaltonControls = {
    drop: (count: number) => void
}

interface GaltonBoardProps {
    rows: number
    multipliers: readonly number[]
    interval: number
    playing: boolean
    label: string
    histogram?: boolean
    compact?: boolean
    className?: string
    soundRef?: MutableRefObject<boolean>
    controlsRef?: MutableRefObject<GaltonControls | null>
    onLand?: (event: LandEvent) => void
}

type Point = {
    x: number
    y: number
}

type Ball = {
    points: Point[]
    durations: number[]
    total: number
    elapsed: number
    reached: number
    offsets: number[]
    bin: number
    trail: Point[]
}

type Geometry = {
    width: number
    height: number
    gap: number
    gapY: number
    top: number
    cx: number
    pegRadius: number
    ballRadius: number
    binTop: number
    binWidth: number
    binHeight: number
    histTop: number
    histHeight: number
}

type Engine = {
    drop: (count: number) => void
    wake: () => void
    redraw: () => void
}

const EMBER = '#FF5B1F'
const INK = '#08080A'
const BIN_REST = '#1C1C22'
const TRAIL_LENGTH = 9
const MAX_BALLS = 48

export const formatBin = (value: number): string => {
    if (value >= 100) return String(Math.round(value))
    if (value >= 10) return String(Math.round(value * 10) / 10)
    return String(Math.round(value * 100) / 100)
}

const binomial = (n: number, k: number): number => {
    let result = 1
    for (let i = 1; i <= k; i++) result = (result * (n - k + i)) / i
    return result
}

/**
 * A Galton board drawn on canvas. Every ball's path is decided the moment it spawns,
 * then played back as projectile hops between pegs: x moves linearly, y follows a parabola.
 */
const GaltonBoard: FC<GaltonBoardProps> = ({
    rows,
    multipliers,
    interval,
    playing,
    label,
    histogram = false,
    compact = false,
    className,
    soundRef,
    controlsRef,
    onLand
}) => {
    const wrapRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const engineRef = useRef<Engine | null>(null)
    const playingRef = useRef(playing)
    const multipliersRef = useRef(multipliers)
    const onLandRef = useRef(onLand)
    const soundHolder = useRef(soundRef)

    useEffect(() => {
        onLandRef.current = onLand
        soundHolder.current = soundRef
    }, [onLand, soundRef])

    useEffect(() => {
        multipliersRef.current = multipliers
        engineRef.current?.redraw()
    }, [multipliers])

    useEffect(() => {
        playingRef.current = playing
        engineRef.current?.wake()
    }, [playing])

    useEffect(() => {
        if (!controlsRef) return

        controlsRef.current = {
            drop: count => engineRef.current?.drop(count)
        }

        return () => {
            controlsRef.current = null
        }
    }, [controlsRef])

    useEffect(() => {
        const wrap = wrapRef.current
        const canvas = canvasRef.current
        const context = canvas?.getContext('2d')

        if (!wrap || !canvas || !context) return

        const ctx = context
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const mono = getComputedStyle(document.documentElement).getPropertyValue('--font-app-mono').trim() || 'ui-monospace, monospace'

        const logs = multipliersRef.current.map(value => Math.log(Math.max(value, 0.01)))
        const minLog = Math.min(...logs)
        const maxLog = Math.max(...logs)
        const intensity = logs.map(value => (maxLog === minLog ? 0 : (value - minLog) / (maxLog - minLog)))
        const binFill = intensity.map(value => mix(BIN_REST, EMBER, Math.pow(value, 1.1)))
        const binInk = intensity.map(value => (value > 0.52 ? INK : 'rgba(244, 242, 238, 0.78)'))

        const glow = Array.from({ length: rows }, (_, row) => new Array<number>(row + 3).fill(0))
        const counts = new Array<number>(rows + 1).fill(0)
        const flashes = new Array<number>(rows + 1).fill(0)
        const balls: Ball[] = []
        const timers = new Set<number>()

        if (reduced && histogram) {
            const total = Math.pow(2, rows)
            for (let k = 0; k <= rows; k++) counts[k] = Math.round((binomial(rows, k) / total) * 400)
        }

        let geometry: Geometry | null = null
        let frame = 0
        let last = 0
        let sinceDrop = interval * 0.7
        let onScreen = true
        let pageVisible = !document.hidden

        const roundRect = (x: number, y: number, width: number, height: number, radius: number) => {
            ctx.beginPath()
            if (typeof ctx.roundRect === 'function') ctx.roundRect(x, y, width, height, radius)
            else ctx.rect(x, y, width, height)
        }

        const draw = () => {
            const g = geometry
            if (!g) return

            ctx.clearRect(0, 0, g.width, g.height)

            ctx.fillStyle = 'rgba(244, 242, 238, 0.3)'
            ctx.beginPath()

            for (let row = 0; row < rows; row++) {
                const y = g.top + row * g.gapY

                for (let i = 0; i < row + 3; i++) {
                    if (glow[row][i] > 0.02) continue
                    const x = g.cx + (i - (row + 2) / 2) * g.gap
                    ctx.moveTo(x + g.pegRadius, y)
                    ctx.arc(x, y, g.pegRadius, 0, Math.PI * 2)
                }
            }

            ctx.fill()

            for (let row = 0; row < rows; row++) {
                const y = g.top + row * g.gapY

                for (let i = 0; i < row + 3; i++) {
                    const lit = glow[row][i]
                    if (lit <= 0.02) continue

                    const x = g.cx + (i - (row + 2) / 2) * g.gap
                    const radius = g.pegRadius * (2.5 + lit * 5)
                    const halo = ctx.createRadialGradient(x, y, 0, x, y, radius)
                    halo.addColorStop(0, `rgba(255, 91, 31, ${0.6 * lit})`)
                    halo.addColorStop(1, 'rgba(255, 91, 31, 0)')
                    ctx.fillStyle = halo
                    ctx.beginPath()
                    ctx.arc(x, y, radius, 0, Math.PI * 2)
                    ctx.fill()

                    ctx.fillStyle = `rgba(255, ${Math.round(242 - 120 * lit)}, ${Math.round(238 - 190 * lit)}, ${0.35 + 0.65 * lit})`
                    ctx.beginPath()
                    ctx.arc(x, y, g.pegRadius * (1 + lit * 0.4), 0, Math.PI * 2)
                    ctx.fill()
                }
            }

            ctx.lineCap = 'round'

            for (const ball of balls) {
                const trail = ball.trail

                for (let i = 1; i < trail.length; i++) {
                    const t = i / trail.length
                    ctx.strokeStyle = `rgba(255, 91, 31, ${t * 0.45})`
                    ctx.lineWidth = g.ballRadius * (0.35 + t * 1.2)
                    ctx.beginPath()
                    ctx.moveTo(trail[i - 1].x, trail[i - 1].y)
                    ctx.lineTo(trail[i].x, trail[i].y)
                    ctx.stroke()
                }

                const head = trail[trail.length - 1]
                if (!head) continue

                const haloRadius = g.ballRadius * 3.4
                const halo = ctx.createRadialGradient(head.x, head.y, 0, head.x, head.y, haloRadius)
                halo.addColorStop(0, 'rgba(255, 91, 31, 0.42)')
                halo.addColorStop(1, 'rgba(255, 91, 31, 0)')
                ctx.fillStyle = halo
                ctx.beginPath()
                ctx.arc(head.x, head.y, haloRadius, 0, Math.PI * 2)
                ctx.fill()

                const core = ctx.createRadialGradient(head.x - g.ballRadius * 0.35, head.y - g.ballRadius * 0.35, 0, head.x, head.y, g.ballRadius)
                core.addColorStop(0, '#FFFFFF')
                core.addColorStop(0.5, '#FFD3C0')
                core.addColorStop(1, EMBER)
                ctx.fillStyle = core
                ctx.beginPath()
                ctx.arc(head.x, head.y, g.ballRadius, 0, Math.PI * 2)
                ctx.fill()
            }

            const values = multipliersRef.current
            const fontSize = Math.max(7.5, Math.min(12, g.gap * 0.3))

            ctx.font = `600 ${fontSize}px ${mono}`
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'

            for (let k = 0; k <= rows; k++) {
                const x = g.cx + (k - rows / 2) * g.gap
                const flash = flashes[k]
                const y = g.binTop + Math.sin(flash * Math.PI) * g.binHeight * 0.18
                const middle = y + g.binHeight / 2

                ctx.fillStyle = flash > 0.01 ? mix(binFill[k], '#FFFFFF', flash * 0.4) : binFill[k]
                roundRect(x - g.binWidth / 2, y, g.binWidth, g.binHeight, Math.min(6, g.binWidth * 0.22))
                ctx.fill()

                if (flash > 0.01) {
                    const spread = g.binWidth * 1.5
                    const halo = ctx.createRadialGradient(x, middle, 0, x, middle, spread)
                    halo.addColorStop(0, `rgba(255, 91, 31, ${0.32 * flash})`)
                    halo.addColorStop(1, 'rgba(255, 91, 31, 0)')
                    ctx.fillStyle = halo
                    ctx.fillRect(x - spread, middle - spread, spread * 2, spread * 2)
                }

                ctx.fillStyle = binInk[k]
                ctx.fillText(formatBin(values[k] ?? 0), x, middle + 0.5)
            }

            if (histogram) {
                const peak = Math.max(10, ...counts)
                const baseline = g.histTop + g.histHeight
                const width = g.binWidth * 0.5

                ctx.fillStyle = 'rgba(244, 242, 238, 0.08)'
                ctx.fillRect(g.cx - (rows / 2 + 0.5) * g.gap, baseline, (rows + 1) * g.gap, 1)

                for (let k = 0; k <= rows; k++) {
                    const height = (counts[k] / peak) * g.histHeight
                    if (height < 0.5) continue

                    const x = g.cx + (k - rows / 2) * g.gap
                    const gradient = ctx.createLinearGradient(0, baseline - height, 0, baseline)
                    gradient.addColorStop(0, `rgba(255, 91, 31, ${0.55 + 0.45 * flashes[k]})`)
                    gradient.addColorStop(1, 'rgba(255, 91, 31, 0.08)')
                    ctx.fillStyle = gradient
                    roundRect(x - width / 2, baseline - height, width, height, Math.min(2, width / 2))
                    ctx.fill()
                }
            }
        }

        const measure = () => {
            const rect = wrap.getBoundingClientRect()
            if (rect.width < 10 || rect.height < 10) return

            const dpr = Math.min(window.devicePixelRatio || 1, 2)
            canvas.width = Math.round(rect.width * dpr)
            canvas.height = Math.round(rect.height * dpr)
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

            const histHeight = histogram ? Math.max(26, rect.height * 0.1) : 0
            const histGap = histogram ? 12 : 0
            const ratio = 0.86
            const binRatio = 0.72
            const lead = 1.1
            const vertical = rect.height - histHeight - histGap - (compact ? 6 : 24)
            const byHeight = vertical / (lead + (rows - 1) * ratio + ratio * 0.9 + binRatio)
            const byWidth = (rect.width * (compact ? 0.96 : 0.92)) / (rows + 2)
            const gap = Math.max(6, Math.min(byHeight, byWidth))
            const gapY = gap * ratio
            const binHeight = gap * binRatio
            const boardHeight = gap * lead + (rows - 1) * gapY + gapY * 0.9 + binHeight + histGap + histHeight
            const top = (rect.height - boardHeight) / 2 + gap * lead
            const binTop = top + (rows - 1) * gapY + gapY * 0.9

            geometry = {
                width: rect.width,
                height: rect.height,
                gap,
                gapY,
                top,
                cx: rect.width / 2,
                pegRadius: Math.max(1.5, gap * 0.08),
                ballRadius: Math.max(2.4, gap * 0.19),
                binTop,
                binWidth: gap * 0.86,
                binHeight,
                histTop: binTop + binHeight + histGap,
                histHeight
            }

            balls.length = 0
            draw()
        }

        const createBall = (): Ball | null => {
            const g = geometry
            if (!g) return null

            const offsets = [0]
            let offset = 0

            for (let row = 1; row < rows; row++) {
                offset += Math.random() < 0.5 ? -0.5 : 0.5
                offsets.push(offset)
            }

            const final = offset + (Math.random() < 0.5 ? -0.5 : 0.5)
            const lift = g.pegRadius + g.ballRadius + 0.6
            const points: Point[] = [{ x: g.cx + (Math.random() - 0.5) * g.gap * 0.1, y: g.top - g.gap * 0.95 }]

            offsets.forEach((value, row) => {
                points.push({ x: g.cx + value * g.gap, y: g.top + row * g.gapY - lift })
            })

            points.push({ x: g.cx + final * g.gap, y: g.binTop + g.binHeight * 0.35 })

            const pace = (compact ? 118 : 100) * (0.9 + Math.random() * 0.2)
            const segments = points.length - 1
            const durations = Array.from({ length: segments }, (_, index) => {
                if (index === 0) return pace * 1.35
                if (index === segments - 1) return pace * 1.05
                return pace * (1 - Math.min(0.22, index * 0.013))
            })

            return {
                points,
                durations,
                total: durations.reduce((sum, value) => sum + value, 0),
                elapsed: 0,
                reached: 0,
                offsets,
                bin: Math.round(final + rows / 2),
                trail: []
            }
        }

        const positionOf = (ball: Ball): Point => {
            let remaining = ball.elapsed
            let index = 0

            while (index < ball.durations.length - 1 && remaining > ball.durations[index]) {
                remaining -= ball.durations[index]
                index += 1
            }

            const u = Math.min(1, Math.max(0, remaining / ball.durations[index]))
            const from = ball.points[index]
            const to = ball.points[index + 1]

            if (index === 0) return { x: from.x + (to.x - from.x) * u, y: from.y + (to.y - from.y) * u * u }

            const hop = (geometry?.gapY ?? 10) * (index === ball.durations.length - 1 ? 0.2 : 0.42)
            return { x: from.x + (to.x - from.x) * u, y: from.y + (to.y - from.y) * u - hop * 4 * u * (1 - u) }
        }

        const completed = (ball: Ball): number => {
            let elapsed = 0
            let count = 0

            for (const duration of ball.durations) {
                if (ball.elapsed < elapsed + duration) break
                elapsed += duration
                count += 1
            }

            return count
        }

        const contact = (ball: Ball, row: number) => {
            const peg = Math.round(ball.offsets[row] + (row + 2) / 2)
            if (glow[row] && peg >= 0 && peg < glow[row].length) glow[row][peg] = 1
            if (soundHolder.current?.current) playTick(Math.round(((ball.offsets[row] + rows / 2) / rows) * 9))
        }

        const settle = (bin: number) => {
            if (bin < 0 || bin > rows) return

            counts[bin] += 1
            flashes[bin] = 1

            const value = intensity[bin] ?? 0
            onLandRef.current?.({ bin, multiplier: multipliersRef.current[bin] ?? 0, intensity: value })
            if (soundHolder.current?.current) playChime(value)
        }

        const busy = (): boolean => {
            return balls.length > 0 || flashes.some(value => value > 0.01) || glow.some(row => row.some(value => value > 0.01))
        }

        const step = (now: number) => {
            frame = 0

            const dt = last ? Math.min(50, now - last) : 16
            last = now

            if (playingRef.current) {
                sinceDrop += dt

                if (sinceDrop >= interval) {
                    sinceDrop = 0
                    const ball = createBall()
                    if (ball && balls.length < MAX_BALLS) balls.push(ball)
                }
            }

            for (let index = balls.length - 1; index >= 0; index--) {
                const ball = balls[index]
                ball.elapsed += dt

                const reached = completed(ball)

                while (ball.reached < reached) {
                    ball.reached += 1
                    if (ball.reached <= rows) contact(ball, ball.reached - 1)
                }

                if (ball.elapsed >= ball.total) {
                    settle(ball.bin)
                    balls.splice(index, 1)
                    continue
                }

                ball.trail.push(positionOf(ball))
                if (ball.trail.length > TRAIL_LENGTH) ball.trail.shift()
            }

            const glowFade = dt / 420
            const flashFade = dt / 600

            for (const row of glow) {
                for (let i = 0; i < row.length; i++) {
                    if (row[i] > 0) row[i] = Math.max(0, row[i] - glowFade)
                }
            }

            for (let k = 0; k < flashes.length; k++) {
                if (flashes[k] > 0) flashes[k] = Math.max(0, flashes[k] - flashFade)
            }

            draw()

            if (onScreen && pageVisible && (playingRef.current || busy())) {
                frame = requestAnimationFrame(step)
            }
        }

        const wake = () => {
            if (frame || reduced || !onScreen || !pageVisible || !geometry) return
            last = 0
            frame = requestAnimationFrame(step)
        }

        const settleInstantly = () => {
            let offset = 0
            for (let row = 0; row < rows; row++) offset += Math.random() < 0.5 ? -0.5 : 0.5
            settle(Math.round(offset + rows / 2))
            flashes.fill(0)
        }

        engineRef.current = {
            drop: count => {
                if (reduced) {
                    for (let i = 0; i < count; i++) settleInstantly()
                    draw()
                    return
                }

                for (let i = 0; i < count; i++) {
                    const id = window.setTimeout(() => {
                        timers.delete(id)
                        const ball = createBall()
                        if (ball && balls.length < MAX_BALLS) balls.push(ball)
                        wake()
                    }, i * 75)
                    timers.add(id)
                }
            },
            wake,
            redraw: () => {
                if (!frame) draw()
            }
        }

        const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure())
        resizeObserver?.observe(wrap)

        const intersectionObserver = typeof IntersectionObserver === 'undefined'
            ? null
            : new IntersectionObserver(([entry]) => {
                onScreen = entry ? entry.isIntersecting : true
                if (onScreen) wake()
            }, { rootMargin: '80px' })
        intersectionObserver?.observe(wrap)

        const onVisibility = () => {
            pageVisible = !document.hidden
            if (pageVisible) wake()
        }

        document.addEventListener('visibilitychange', onVisibility)

        if (document.fonts) {
            void document.fonts.ready.then(() => {
                if (!frame) draw()
            })
        }

        measure()
        wake()

        return () => {
            cancelAnimationFrame(frame)
            frame = 0
            resizeObserver?.disconnect()
            intersectionObserver?.disconnect()
            document.removeEventListener('visibilitychange', onVisibility)
            timers.forEach(id => window.clearTimeout(id))
            timers.clear()
            engineRef.current = null
        }
    }, [rows, interval, histogram, compact])

    return (
        <div
            ref={wrapRef}
            onPointerDown={() => engineRef.current?.drop(compact ? 3 : 6)}
            className={cn('relative select-none', className)}
        >
            <canvas ref={canvasRef} role='img' aria-label={label} className='absolute inset-0 size-full' />
        </div>
    )
}

export default memo(GaltonBoard)
